import { isDate } from 'date-fns';
import type { Activity, ActivityInstance, SubtaskItem } from '@api';

/**
 * Rutiner — ren logik, inga hooks och inga sidoeffekter.
 *
 * En RUTIN är en aktivitet (`isRoutine`) vars delsteg är STEG som brukaren går
 * igenom ett i taget, med egen tid per steg (`durationMinutes`). Rutinen är i
 * övrigt en vanlig aktivitet: den ligger på en tid, kan upprepas och blir en
 * instans per dag precis som allt annat i kalendern.
 *
 * HUR LÅNGT BRUKAREN HAR KOMMIT lagras inte i något nytt fält utan i instansens
 * `checkedSubtasks` — samma lista som delstegen redan bockas av i, och samma
 * endpoint (`setSubTask`). Därför följer läget automatiskt med till varje vy som
 * har instansen, det överlever en omladdning, och "Nästa" behöver ingen egen
 * lagring: att gå vidare ÄR att bocka av steget man står på.
 *
 * Aktuellt steg = första steget i ordning som inte är avbockat. Alla avbockade →
 * rutinen är genomgången (null), och då är det "Klar" som gäller, inte "Nästa".
 */

/** Ett steg i en rutin: ett delsteg där tiden är obligatorisk. */
export type RoutineStep = SubtaskItem & { durationMinutes: number };

/** Grundlängd i minuter för ett steg som (mot förväntan) saknar tid. */
export const DEFAULT_STEP_MINUTES = 5;

export const isRoutine = (activity?: Activity | null): boolean =>
  !!activity?.isRoutine && (activity.subtasks?.length ?? 0) > 0;

/**
 * Stegen i ordning. Positionen är sanningen om ordningen (samma som delstegslistan
 * använder); saknad tid faller tillbaka på grundlängden i stället för att steget
 * tappas — ett steg utan tid är fortfarande ett steg brukaren ska göra.
 */
export const getRoutineSteps = (activity?: Activity | null): RoutineStep[] =>
  [...(activity?.subtasks ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((step) => ({
      ...step,
      durationMinutes:
        step.durationMinutes && step.durationMinutes > 0
          ? step.durationMinutes
          : DEFAULT_STEP_MINUTES,
    }));

const isChecked = (step: SubtaskItem, checkedSubtasks?: number[]): boolean =>
  !!checkedSubtasks?.some((id) => id === step.subtaskId);

/** Index (0-baserat) för aktuellt steg, eller stegens antal när allt är avbockat. */
export const getCurrentStepIndex = (steps: RoutineStep[], checkedSubtasks?: number[]): number => {
  const index = steps.findIndex((step) => !isChecked(step, checkedSubtasks));
  return index === -1 ? steps.length : index;
};

/** Steget brukaren står på just nu. null = alla steg är avbockade. */
export const getCurrentStep = (
  steps: RoutineStep[],
  checkedSubtasks?: number[],
): RoutineStep | null => steps[getCurrentStepIndex(steps, checkedSubtasks)] ?? null;

/** Om det finns ett steg EFTER det aktuella (avgör Nästa kontra Klar). */
export const hasNextStep = (steps: RoutineStep[], checkedSubtasks?: number[]): boolean =>
  getCurrentStepIndex(steps, checkedSubtasks) < steps.length - 1;

/** Rutinens totala längd = summan av stegen. Används när rutinen skapas. */
export const getRoutineDuration = (steps: { durationMinutes?: number }[]): number =>
  steps.reduce(
    (total, step) =>
      total + (step.durationMinutes && step.durationMinutes > 0 ? step.durationMinutes : 0),
    0,
  );

/**
 * Etiketten "en sak i taget" för listor och veckoschema: rutinens namn plus det
 * steg brukaren står på.
 *
 * Steget läggs bara till för en PÅGÅENDE rutin, och villkoret sitter HÄR i stället
 * för hos varje anropare: en rutin som inte är startad står inte på något steg ännu
 * (då vore "Morgonrutin · Klä på sig" en gissning om framtiden), och en avslutad
 * rutin har inget steg kvar. I båda fallen är rutinens eget namn hela sanningen.
 */
export const getRoutineStepLabel = (
  instance: ActivityInstance,
  activity: Activity | undefined,
  separator = ' · ',
): string => {
  const ongoing = isDate(instance.dateIsStarted) && !isDate(instance.dateIsCompleted);
  if (!ongoing || !isRoutine(activity)) return instance.activityName;
  const step = getCurrentStep(getRoutineSteps(activity), instance.checkedSubtasks);
  if (!step) return instance.activityName;
  return `${instance.activityName}${separator}${step.subtaskText}`;
};

/**
 * Dagslåset: en låst aktivitet får inte startas FÖRE sin dag. Handledaren sätter
 * låset per rutin (activities.day_locked, V011) för att brukaren inte ska kunna göra
 * morgondagens morgonrutin ikväll.
 *
 * Bara framåt. Ligger instansen i dag eller tidigare är den öppen: låset skulle hindra
 * att man springer före, inte att man kommer ikapp något man missat.
 *
 * Regeln finns också i backend (ActivityHandler.startActivity) och DÄR är den bindande.
 * Den här funktionen är till för att vyn ska kunna säga varför knappen inte går att
 * trycka på, i stället för att skicka ett anrop som kommer tillbaka som ett fel
 * brukaren inte kan tolka.
 *
 * Dagen jämförs i enhetens lokala tid, vilket är samma tid som instansens klockslag
 * visas i — det är den dag brukaren faktiskt ser i kalendern.
 */
export const isLockedUntilItsDay = (
  activity: Activity | undefined | null,
  instance: Pick<ActivityInstance, 'startDate'>,
  now: Date = new Date(),
): boolean => {
  if (!activity?.dayLocked) return false;
  const start = new Date(instance.startDate);
  if (Number.isNaN(start.getTime())) return false;
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return startDay.getTime() > today.getTime();
};
